// src/pages/History.js — Admin portal with date range + outstation filter
// Added click-to-sort headers (same visual pattern as Dashboard), sorting
// client-side since History already loads its full result set in one shot.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from '../utils/api';
import { StatusBadge, Spinner, formatCurrency } from '../components/Shared';

// toISOString() is always UTC — using it directly can silently return
// YESTERDAY's date for browsers in timezones ahead of UTC (like IST)
// during late-night/early-morning hours. Adjusting by the browser's own
// timezone offset first gives the correct LOCAL calendar date instead —
// same fix already applied in Reports.js.
function localDateString(d) {
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().split('T')[0];
}
const today   = () => localDateString(new Date());
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate()-n); return localDateString(d); };

const PRESETS = [
  { label:'Today',      from:today(),    to:today() },
  { label:'Yesterday',  from:daysAgo(1), to:daysAgo(1) },
  { label:'Last 7 days',from:daysAgo(7), to:today() },
  { label:'Last 30 days',from:daysAgo(30),to:today() },
];

const STAGE_FILTERS = [
  { value:'',                    label:'All Stages' },
  { value:'stage_1_erp',         label:'ERP Received' },
  { value:'stage_2_stock_removal',label:'Stock Removal' },
  { value:'stage_3_checking',    label:'Checking' },
  { value:'stage_4_packing',     label:'Ready for Delivery' },
  { value:'stage_5_dispatched',  label:'In Transit' },
  { value:'stage_6_delivered',   label:'Delivered' },
  { value:'complete',            label:'Complete' },
  { value:'cancelled',           label:'Cancelled' },
  { value:'reattempt_pending',   label:'Reattempt Pending' },
];

// Pull the numeric part out of an invoice number (e.g. "MNG24186" -> 24186)
// for a natural sort instead of alphabetical string sort.
function mngNumber(invoiceNumber) {
  const digits = (invoiceNumber || '').replace(/[^0-9]/g, '');
  return digits ? parseInt(digits, 10) : 0;
}

export default function History() {
  const [invoices,     setInvoices]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [from,         setFrom]         = useState(today());
  const [to,           setTo]           = useState(today());
  const [stage,        setStage]        = useState('');
  const [search,       setSearch]       = useState('');
  const [total,        setTotal]        = useState(0);
  const [allTime,      setAllTime]      = useState(false);
  const [deliveryType, setDeliveryType] = useState(''); // '' | 'instation' | 'outstation'
  const [sortCol,      setSortCol]      = useState('invoice_number');
  const [sortOrder,    setSortOrder]    = useState('desc');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/invoices?limit=1000&offset=0`;
      if (!allTime) url += `&date_from=${from}&date_to=${to}`;
      if (stage)        url += `&status=${encodeURIComponent(stage)}`;
      if (deliveryType) url += `&delivery_type=${encodeURIComponent(deliveryType)}`;
      // invoice_number alone already OR-matches both invoice_number AND
      // party_name on the backend — also sending party_name here turned
      // that OR into an AND, silently breaking plain invoice-number
      // searches that don't happen to also match a party name.
      if (search)       url += `&invoice_number=${encodeURIComponent(search)}`;
      const data = await get(url);
      setInvoices(data.invoices || []);
      setTotal(data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [from, to, stage, search, allTime, deliveryType]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  function handleSort(col) {
    let newOrder;
    if (col === sortCol) {
      newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    } else {
      newOrder = ['invoice_number', 'received_at', 'value'].includes(col) ? 'desc' : 'asc';
    }
    setSortCol(col);
    setSortOrder(newOrder);
  }

  function sortIcon(col) {
    if (sortCol !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-blue-600 ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  }

  const filtered = invoices.filter(inv =>
    !search ||
    inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
    inv.party_name?.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    switch (sortCol) {
      case 'invoice_number':
        cmp = mngNumber(a.invoice_number) - mngNumber(b.invoice_number);
        break;
      case 'party_name':
        cmp = (a.party_name || '').localeCompare(b.party_name || '');
        break;
      case 'city':
        cmp = (a.city || '').localeCompare(b.city || '');
        break;
      case 'status':
        cmp = (a.status || '').localeCompare(b.status || '');
        break;
      case 'received_at':
        cmp = new Date(a.received_at || 0) - new Date(b.received_at || 0);
        break;
      case 'value':
        cmp = (Number(a.invoice_value) || 0) - (Number(b.invoice_value) || 0);
        break;
      default:
        cmp = 0;
    }
    return sortOrder === 'asc' ? cmp : -cmp;
  });

  const thClass = "text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none";

  return (
    <div className="p-6 max-w-[1600px]">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Invoice History</h1>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-5">
        <div className="flex gap-2 flex-wrap mb-4">
          {PRESETS.map(p => (
            <button key={p.label}
              onClick={() => { setFrom(p.from); setTo(p.to); setAllTime(false); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors
                ${!allTime && from===p.from && to===p.to
                  ? 'bg-blue-900 text-white border-blue-900'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
              {p.label}
            </button>
          ))}
          <button onClick={() => setAllTime(v => !v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors
              ${allTime ? 'bg-green-700 text-white border-green-700' : 'bg-white text-gray-600 border-gray-300 hover:border-green-400'}`}>
            {allTime ? '✓ All Time' : 'All Time'}
          </button>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} disabled={allTime}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} disabled={allTime}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Stage</label>
            <select value={stage} onChange={e => setStage(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {STAGE_FILTERS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
            <div className="flex gap-1">
              {[['','All'],['instation','🏙️ Instation'],['outstation','🚛 Outstation']].map(([val,lbl]) => (
                <button key={val} onClick={() => setDeliveryType(val)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors
                    ${deliveryType === val
                      ? 'bg-blue-900 text-white border-blue-900'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-48">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Search</label>
            <input type="text" placeholder="Invoice # or party name..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <button onClick={load}
            className="px-4 py-2 bg-blue-900 text-white rounded-lg text-sm font-semibold hover:bg-blue-800">
            🔍 Search
          </button>
          <span className="text-sm text-gray-500 self-end pb-0.5">{sorted.length} invoices</span>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : sorted.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-16 text-center">
          <p className="text-4xl mb-3">📋</p>
          <p className="text-gray-500 font-medium">No invoices found</p>
          <p className="text-gray-400 text-sm mt-1">Try adjusting the filters or clearing the search</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th onClick={() => handleSort('invoice_number')} className={thClass}>
                  Invoice #{sortIcon('invoice_number')}
                </th>
                <th onClick={() => handleSort('party_name')} className={thClass}>
                  Party{sortIcon('party_name')}
                </th>
                <th onClick={() => handleSort('city')} className={thClass}>
                  City{sortIcon('city')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th onClick={() => handleSort('status')} className={thClass}>
                  Stage{sortIcon('status')}
                </th>
                <th onClick={() => handleSort('received_at')} className={thClass}>
                  Received{sortIcon('received_at')}
                </th>
                <th onClick={() => handleSort('value')} className={thClass}>
                  Value{sortIcon('value')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(inv => (
                <tr key={inv.invoice_id} onClick={() => navigate(`/invoices/${inv.invoice_id}`)}
                  className="border-t hover:bg-blue-50 cursor-pointer transition-colors">
                  <td className="px-4 py-3 font-semibold text-blue-900">{inv.invoice_number}</td>
                  <td className="px-4 py-3 font-medium">{inv.party_name}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{inv.city}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full
                      ${inv.delivery_type === 'instation' ? 'bg-blue-100 text-blue-700' :
                        inv.delivery_type === 'outstation' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-500'}`}>
                      {inv.delivery_type || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {inv.received_at ? new Date(inv.received_at).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="px-4 py-3 font-medium">{formatCurrency(inv.invoice_value)}</td>
                  <td className="px-4 py-3 text-blue-600 text-xs">View →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
